import { useState, useEffect } from 'react';
import { fetchNote, saveNote as saveNoteAPI } from '../services/noteService';

/**
 * Represents the granularity level of a calendar note.
 * - 'day': A single day's note.
 * - 'week': A note for a calendar week (starting Monday).
 * - 'month': A note for the full calendar month.
 */
export type NoteType = 'day' | 'week' | 'month';

/**
 * Normalizes a date based on the note type.
 *
 * - For 'day', resets time to midnight.
 * - For 'week', adjusts to the Monday of the current week.
 * - For 'month', resets to the first of the month.
 *
 * @param date The original date.
 * @param type The type of note (day, week, or month).
 * @returns A normalized date object used for note retrieval/storage.
 */
const normalizeDateForType = (date: Date, type: NoteType): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  if (type === 'month') {
    d.setDate(1);
  } else if (type === 'week') {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
  }
  return d;
};

/**
 * Custom React hook to manage fetching and saving calendar notes
 * for a given user, date, and note type (day/week/month).
 *
 * @param user The username associated with the note.
 * @param type The type of note being retrieved or saved.
 * @param date The base date used for the note (will be normalized).
 * @returns An object with:
 * - note: The current note content.
 * - setNote: A setter for local note state.
 * - saveNote: A function to persist the note to the backend.
 * - loading: Boolean indicating if the note is being fetched.
 */
export const useCalendarNote = (user: string, type: NoteType, date: Date) => {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setError] = useState<string>('');

  const normalizedDate = normalizeDateForType(date, type);
  const isoDate = normalizedDate.toISOString();

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await fetchNote(user, type, isoDate);
      setNote(data?.content || '');
    } catch (err) {
      setError('Error fetching note');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Saves the provided note content to the backend.
   *
   * @param newContent The content to be saved.
   */
  const saveNote = async (newContent: string) => {
    try {
      await saveNoteAPI(user, type, isoDate, newContent);
      setNote(newContent);
    } catch (err) {
      setError('Error saving note');
    }
  };

  useEffect(() => {
    if (user && type && date) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, date, user]);

  return { note, setNote, saveNote, loading };
};
